import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, LocaleSchema } from "../common/primitives.js";
import { AddressSchema, DisciplineSchema, GovernorateSchema } from "../common/tunisia.js";
const LEGAL_FORMS = [
  "individual",
  "suarl",
  "sarl",
  "sa",
  "snc",
  "scs",
  "gie",
  "other"
];
const LegalFormSchema = z.enum(LEGAL_FORMS);
const OrganizationStatusSchema = z.enum([
  "trial",
  "active",
  "read_only",
  "suspended",
  "closed",
  // REL-BE-01: the tombstone left by the deletion job (no data, no access).
  "deleted"
]);
const CreateOrganizationRequestSchema = z.strictObject({
  legalName: z.string().trim().min(1).max(200),
  tradeName: z.string().trim().min(1).max(200).optional(),
  legalForm: LegalFormSchema,
  taxId: z.string().trim().min(1).max(32),
  disciplines: z.array(DisciplineSchema).min(1).max(8).refine((list) => new Set(list).size === list.length, "disciplines must be distinct"),
  address: AddressSchema,
  governorate: GovernorateSchema,
  professionalRegistration: z.string().trim().min(1).max(50).optional(),
  agreementNumber: z.string().trim().min(1).max(50).optional(),
  defaultLocale: LocaleSchema.default("fr")
});
const OrganizationSchema = z.strictObject({
  id: IdSchema,
  legalName: z.string(),
  tradeName: z.string().nullable(),
  legalForm: LegalFormSchema,
  /** Display form, e.g. "1234567 A / A / M / 000". */
  taxId: z.string(),
  disciplines: z.array(DisciplineSchema),
  address: AddressSchema,
  governorate: GovernorateSchema.nullable(),
  professionalRegistration: z.string().nullable(),
  agreementNumber: z.string().nullable(),
  defaultLocale: LocaleSchema,
  status: OrganizationStatusSchema,
  createdAt: IsoDateTimeSchema,
  version: z.int().min(1),
  /**
   * The organisation space (organisation-wide documents, events and chats; COM-BE-04). Returned by
   * GET /orgs/{orgId}; absent from the create and update answers.
   */
  spaceId: IdSchema.optional()
});
const UpdateOrganizationRequestSchema = z.strictObject({
  legalName: z.string().trim().min(1).max(200),
  tradeName: z.string().trim().min(1).max(200).nullable(),
  legalForm: LegalFormSchema,
  disciplines: z.array(DisciplineSchema).min(1).max(8).refine((list) => new Set(list).size === list.length, "disciplines must be distinct"),
  address: AddressSchema,
  governorate: GovernorateSchema,
  professionalRegistration: z.string().trim().min(1).max(50).nullable(),
  agreementNumber: z.string().trim().min(1).max(50).nullable(),
  defaultLocale: LocaleSchema
}).partial().refine((body) => Object.keys(body).length > 0, "change at least one field");
const DOMAIN_LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const DomainSchema = z.string().trim().toLowerCase().max(253).refine((domain) => {
  const labels = domain.split(".");
  const tld = labels.at(-1) ?? "";
  return labels.length >= 2 && /^[a-z]{2,63}$/.test(tld) && labels.slice(0, -1).every((label) => DOMAIN_LABEL.test(label));
}, "not a domain");
const SharingPolicySchema = z.strictObject({
  /** Members joining another organisation's space need an administrator's approval. */
  requireApprovalForExternalSpaces: z.boolean(),
  /** Guests may only be invited from these email domains (empty: any domain). */
  allowedGuestDomains: z.array(DomainSchema).max(50),
  /** Project managers may share their projects' spaces with partners. */
  projectManagersCanShare: z.boolean()
});
const SecurityPolicySchema = z.strictObject({
  /** Sessions end after this many hours (1–12); null = the platform default (12 h). */
  sessionAbsoluteHours: z.int().min(1).max(12).nullable(),
  /** Every member needs a second factor, not only owners, admins and accountants. */
  mfaForAllMembers: z.boolean()
});
const OrganizationPoliciesSchema = z.strictObject({
  sharing: SharingPolicySchema,
  security: SecurityPolicySchema,
  version: z.int().min(1)
});
const UpdatePoliciesRequestSchema = z.strictObject({
  sharing: SharingPolicySchema.partial().optional(),
  security: SecurityPolicySchema.partial().optional()
}).refine((body) => body.sharing !== void 0 || body.security !== void 0, "change something");
export * from "./directory.js";
export {
  CreateOrganizationRequestSchema,
  LEGAL_FORMS,
  LegalFormSchema,
  OrganizationPoliciesSchema,
  OrganizationSchema,
  OrganizationStatusSchema,
  SecurityPolicySchema,
  SharingPolicySchema,
  UpdateOrganizationRequestSchema,
  UpdatePoliciesRequestSchema
};
//# sourceMappingURL=index.js.map